/**
 * Web Worker 用于 Gaussian Splat 异步排序
 *
 * 基于 PlayCanvas 的 gsplat-unified-sort-worker.js 实现
 * 支持 Linear Sort 和 Radial Sort，使用分层精度权重系统
 */

// 导出 Worker 函数（用于主线程创建 Worker）
export function UnifiedSortWorker(): void {
  /** 排序参数（每个 Splat 的相机空间参数） */
  interface SplatSortParams {
    id: number
    transformedDirection: { x: number; y: number; z: number }
    transformedPosition: { x: number; y: number; z: number }
    offset: number
    scale: number
    aabbMin: [number, number, number]
    aabbMax: [number, number, number]
    modelMatrix?: number[]
    modelViewProjection?: number[]
  }

  /** Centers 数据配置 */
  interface CentersData {
    textureSize: number
    intervals: number[][]
    pixelOffsets: number[][]
    version: number
    totalActiveSplats: number
    ids: number[]
  }

  interface SortVisibilityCompactOptions {
    enabled: boolean
    behindMargin: number
    screenCompact?: boolean
    screenMargin?: number
  }

  /** Worker 接收的消息类型 */
  type WorkerMessage =
    | { command: 'addCenters'; id: number; centers: ArrayBuffer; bounds?: ArrayBuffer }
    | { command: 'removeCenters'; id: number }
    | {
        command: 'sort'
        sortParams: SplatSortParams[]
        radialSorting: boolean
        visibilityCompact?: SortVisibilityCompactOptions
        sortGeneration?: number
        order: ArrayBuffer
      }
    | {
        command: 'intervals'
        textureSize: number
        ids: number[]
        pixelOffsets: number[][]
        intervals: number[][]
        totalActiveSplats: number
        version: number
      }

  /** Worker 发送的消息类型 */
  interface WorkerResponse {
    order: ArrayBuffer
    count: number
    version: number
    sortGeneration?: number
    totalActiveSplats?: number
    culledSplats?: number
    timeMs?: number
    failed?: boolean
  }

  type SortMessage = Extract<WorkerMessage, { command: 'sort' }>

  // Centers 缓存（id → Float32Array）
  const centersMap = new Map<number, Float32Array>()
  const boundsMap = new Map<number, Float32Array>()

  const SORT_BOUNDS_STRIDE = 4
  const SORT_BOUND_HAS_RADIUS = 1
  const SORT_BOUND_HAS_OPACITY = 2
  const SORT_BOUND_HAS_COVARIANCE = 4
  const SORT_OPACITY_CLIP = 1 / 255
  const SORT_OPACITY_CLIP_EPSILON = 1e-7
  const SORT_COVARIANCE_RADIUS_SIGMA = 2

  // 排序临时数据
  let centersData: CentersData | null = null
  let distances: Uint32Array | null = null
  /** world version 内稳定的 compact active index → WorkBuffer texel 映射。 */
  let indexMap: Uint32Array | null = null
  /** visibility compact 后交给 counting sort 的 operation-local 映射。 */
  let sortIndexMap: Uint32Array | null = null
  let countBuffer: Uint32Array | null = null

  function buildIndexMap(data: CentersData): Uint32Array | null {
    const { ids, pixelOffsets, intervals, totalActiveSplats } = data
    if (
      ids.length !== intervals.length ||
      ids.length !== pixelOffsets.length ||
      !Number.isSafeInteger(totalActiveSplats) ||
      totalActiveSplats < 0
    ) {
      throw new Error('GSplat intervals payload 维度非法')
    }

    const next = new Uint32Array(totalActiveSplats)
    let compactIndex = 0
    for (let splatIndex = 0; splatIndex < ids.length; splatIndex++) {
      const declaredIntervals = intervals[splatIndex]
      const centers = centersMap.get(ids[splatIndex])
      if (declaredIntervals.length === 0 && !centers) return null
      const activeIntervals =
        declaredIntervals.length > 0 ? declaredIntervals : [0, (centers?.length ?? 0) / 3]
      const offsets = pixelOffsets[splatIndex]
      if (activeIntervals.length % 2 !== 0 || offsets.length !== activeIntervals.length / 2) {
        throw new Error(`GSplat interval offsets 不匹配: index=${splatIndex}`)
      }
      for (let intervalIndex = 0; intervalIndex < activeIntervals.length; intervalIndex += 2) {
        const start = activeIntervals[intervalIndex]
        const end = activeIntervals[intervalIndex + 1]
        let workBufferIndex = offsets[intervalIndex / 2]
        if (
          !Number.isSafeInteger(start) ||
          !Number.isSafeInteger(end) ||
          start < 0 ||
          end < start ||
          !Number.isSafeInteger(workBufferIndex) ||
          workBufferIndex < 0
        ) {
          throw new Error(`GSplat interval layout 非法: index=${splatIndex}`)
        }
        for (let sourceIndex = start; sourceIndex < end; sourceIndex++) {
          if (compactIndex >= totalActiveSplats) {
            throw new Error('GSplat intervals 超出 totalActiveSplats')
          }
          next[compactIndex++] = workBufferIndex++
        }
      }
    }
    if (compactIndex !== totalActiveSplats) {
      throw new Error(
        `GSplat intervals 未覆盖 totalActiveSplats: actual=${compactIndex}, required=${totalActiveSplats}`
      )
    }
    return next
  }

  function getErrorMessage(error: unknown): string {
    return error instanceof Error ? error.message : String(error)
  }

  // Worker 内联日志：经 postMessage 回传主线程统一过滤输出（不能 import 模块、且 console 在 prod 会被 removeConsole 删除）。
  // level 与 LogLevel 对齐：1=ERROR，4=DEBUG。
  const __LOG_NS = 'gsplat:sort'
  function __emitLog(level: number, message: string): void {
    const now =
      typeof self !== 'undefined' && self.performance && typeof self.performance.now === 'function'
        ? self.performance.now()
        : 0
    self.postMessage({ __log__: { level, namespace: __LOG_NS, message, timestamp: now } })
  }

  function postEmptySortResult(msgData: SortMessage, version: number, failed = false): void {
    const totalActiveSplats = centersData?.totalActiveSplats ?? 0
    const response: WorkerResponse = {
      order: msgData.order,
      count: 0,
      version,
      sortGeneration: msgData.sortGeneration,
      totalActiveSplats,
      culledSplats: totalActiveSplats,
      timeMs: 0,
      failed,
    }
    self.postMessage(response, [msgData.order])
  }

  // 排序模式：false = Linear Sort，true = Radial Sort
  let _radialSort = false
  let _warnedSortKeyOverflow = false

  // 分层精度权重系统
  const NUM_BINS = 32
  const binBase = new Array<number>(NUM_BINS + 1).fill(0)
  const binDivider = new Array<number>(NUM_BINS + 1).fill(0)

  // 权重分级：距离相机的 bin 距离 → 权重倍数
  const weightTiers = [
    { maxDistance: 0, weight: 40.0 }, // 相机 bin
    { maxDistance: 2, weight: 20.0 }, // 相邻 bins
    { maxDistance: 5, weight: 8.0 }, // 附近 bins
    { maxDistance: 10, weight: 3.0 }, // 中等距离
    { maxDistance: Infinity, weight: 1.0 }, // 远处 bins
  ]

  // 预计算权重查找表
  const weightByDistance = new Array<number>(NUM_BINS)
  for (let dist = 0; dist < NUM_BINS; dist++) {
    let weight = 1.0
    for (let j = 0; j < weightTiers.length; j++) {
      if (dist <= weightTiers[j].maxDistance) {
        weight = weightTiers[j].weight
        break
      }
    }
    weightByDistance[dist] = weight
  }

  function shouldKeepForVisibilityCompact(
    params: SplatSortParams,
    x: number,
    y: number,
    z: number,
    compact?: SortVisibilityCompactOptions,
    bounds?: Float32Array,
    boundsIndex?: number
  ): boolean {
    if (!compact?.enabled) {
      return true
    }

    const alphaUpper = getOpacityUpperBound(bounds, boundsIndex)
    if (alphaUpper !== null && alphaUpper <= SORT_OPACITY_CLIP + SORT_OPACITY_CLIP_EPSILON) {
      return false
    }

    const localRadius = getLocalRadiusUpperBound(bounds, boundsIndex)
    const cx = params.transformedPosition.x
    const cy = params.transformedPosition.y
    const cz = params.transformedPosition.z
    const dx = params.transformedDirection.x
    const dy = params.transformedDirection.y
    const dz = params.transformedDirection.z
    const signedDepth = ((x - cx) * dx + (y - cy) * dy + (z - cz) * dz) * params.scale
    const behindMargin = Number.isFinite(compact.behindMargin)
      ? Math.max(0, compact.behindMargin)
      : 0
    const worldRadius =
      localRadius !== null && Number.isFinite(params.scale)
        ? localRadius * Math.abs(params.scale)
        : 0

    if (signedDepth > behindMargin + worldRadius) {
      return false
    }

    if (compact.screenCompact !== true) {
      return true
    }

    const m = params.modelViewProjection
    if (!m || m.length < 16) {
      return true
    }

    if (localRadius === null) {
      return true
    }

    const clipX = m[0] * x + m[4] * y + m[8] * z + m[12]
    const clipY = m[1] * x + m[5] * y + m[9] * z + m[13]
    const clipW = m[3] * x + m[7] * y + m[11] * z + m[15]
    if (
      !Number.isFinite(clipX) ||
      !Number.isFinite(clipY) ||
      !Number.isFinite(clipW) ||
      clipW <= 1e-6
    ) {
      return true
    }

    const screenMargin = Number.isFinite(compact.screenMargin)
      ? Math.max(0, compact.screenMargin ?? 1)
      : 1
    const ndcX = clipX / clipW
    const ndcY = clipY / clipW
    const projectedRadius = projectLocalRadiusToNdc(m, clipW, ndcX, ndcY, localRadius)
    if (!projectedRadius) {
      return true
    }

    const ndcLimitX = 1 + screenMargin + projectedRadius.x
    const ndcLimitY = 1 + screenMargin + projectedRadius.y

    return Math.abs(ndcX) <= ndcLimitX && Math.abs(ndcY) <= ndcLimitY
  }

  function getLocalRadiusUpperBound(bounds?: Float32Array, index?: number): number | null {
    if (!bounds || index === undefined) {
      return null
    }

    const base = index * SORT_BOUNDS_STRIDE
    if (base + SORT_BOUNDS_STRIDE > bounds.length) {
      return null
    }

    const flags = bounds[base + 3] || 0
    const radius = bounds[base]
    if ((flags & SORT_BOUND_HAS_RADIUS) !== 0 && Number.isFinite(radius) && radius > 0) {
      return radius
    }

    const covarianceUpper = bounds[base + 2]
    if (
      (flags & SORT_BOUND_HAS_COVARIANCE) !== 0 &&
      Number.isFinite(covarianceUpper) &&
      covarianceUpper > 0
    ) {
      return Math.sqrt(covarianceUpper) * SORT_COVARIANCE_RADIUS_SIGMA
    }

    return null
  }

  function getOpacityUpperBound(bounds?: Float32Array, index?: number): number | null {
    if (!bounds || index === undefined) {
      return null
    }

    const base = index * SORT_BOUNDS_STRIDE
    if (base + SORT_BOUNDS_STRIDE > bounds.length) {
      return null
    }

    const flags = bounds[base + 3] || 0
    const opacityUpper = bounds[base + 1]
    if ((flags & SORT_BOUND_HAS_OPACITY) === 0 || !Number.isFinite(opacityUpper)) {
      return null
    }

    return opacityUpper
  }

  function projectLocalRadiusToNdc(
    m: number[],
    clipW: number,
    ndcX: number,
    ndcY: number,
    localRadius: number
  ): { x: number; y: number } | null {
    if (!Number.isFinite(localRadius) || localRadius <= 0) {
      return null
    }

    const clipRadiusX = localRadius * Math.hypot(m[0], m[4], m[8])
    const clipRadiusY = localRadius * Math.hypot(m[1], m[5], m[9])
    const clipRadiusW = localRadius * Math.hypot(m[3], m[7], m[11])
    const denominator = clipW - clipRadiusW
    if (!Number.isFinite(denominator) || denominator <= 1e-6) {
      return null
    }

    return {
      x: (clipRadiusX + Math.abs(ndcX) * clipRadiusW) / denominator,
      y: (clipRadiusY + Math.abs(ndcY) * clipRadiusW) / denominator,
    }
  }

  /**
   * 设置相机相对的 bin 权重（PlayCanvas 核心优化）
   */
  function setupCameraRelativeBins(cameraBin: number, bucketCount: number): void {
    const bitsPerBin: number[] = []

    // 根据距离相机的 bin 距离分配权重
    for (let i = 0; i < NUM_BINS; i++) {
      const distFromCamera = Math.abs(i - cameraBin)
      bitsPerBin[i] = weightByDistance[distFromCamera]
    }

    // 归一化到 bucket 预算
    const totalWeight = bitsPerBin.reduce((a, b) => a + b, 0)
    let accumulated = 0

    for (let i = 0; i < NUM_BINS; i++) {
      binDivider[i] = Math.max(1, Math.floor((bitsPerBin[i] / totalWeight) * bucketCount))
      binBase[i] = accumulated
      accumulated += binDivider[i]
    }

    // 调整最后一个 bin 以确保精确匹配
    if (accumulated > bucketCount) {
      const excess = accumulated - bucketCount
      binDivider[NUM_BINS - 1] = Math.max(1, binDivider[NUM_BINS - 1] - excess)
    }

    // 添加安全条目（浮点误差可能导致 bin >= NUM_BINS）
    binBase[NUM_BINS] = binBase[NUM_BINS - 1] + binDivider[NUM_BINS - 1]
    binDivider[NUM_BINS] = 0
  }

  /**
   * 计算有效距离范围（Linear Sort）- 使用优化的 2 次点积方法
   */
  function computeEffectiveDistanceRangeLinear(sortParams: SplatSortParams[]): {
    minDist: number
    maxDist: number
  } {
    let minDist = Infinity
    let maxDist = -Infinity

    for (let paramIdx = 0; paramIdx < sortParams.length; paramIdx++) {
      const params = sortParams[paramIdx]
      const { transformedDirection, offset, scale, aabbMin, aabbMax } = params
      const dx = transformedDirection.x
      const dy = transformedDirection.y
      const dz = transformedDirection.z

      // PlayCanvas 优化：根据方向向量符号选择 AABB 的 min/max 点
      const pxMin = dx >= 0 ? aabbMin[0] : aabbMax[0]
      const pyMin = dy >= 0 ? aabbMin[1] : aabbMax[1]
      const pzMin = dz >= 0 ? aabbMin[2] : aabbMax[2]
      const pxMax = dx >= 0 ? aabbMax[0] : aabbMin[0]
      const pyMax = dy >= 0 ? aabbMax[1] : aabbMin[1]
      const pzMax = dz >= 0 ? aabbMax[2] : aabbMin[2]

      const dMin = pxMin * dx + pyMin * dy + pzMin * dz
      const dMax = pxMax * dx + pyMax * dy + pzMax * dz

      const eMin = dMin * scale + offset
      const eMax = dMax * scale + offset

      // 处理负缩放（交换 min/max）
      const localMin = Math.min(eMin, eMax)
      const localMax = Math.max(eMin, eMax)

      if (localMin < minDist) minDist = localMin
      if (localMax > maxDist) maxDist = localMax
    }

    if (minDist === Infinity) {
      minDist = 0
      maxDist = 0
    }

    return { minDist, maxDist }
  }

  /**
   * 计算有效距离范围（Radial Sort）
   */
  function computeEffectiveDistanceRangeRadial(sortParams: SplatSortParams[]): {
    minDist: number
    maxDist: number
  } {
    let maxDist = -Infinity

    for (let paramIdx = 0; paramIdx < sortParams.length; paramIdx++) {
      const params = sortParams[paramIdx]
      const { transformedPosition, scale, aabbMin, aabbMax } = params
      const cx = transformedPosition.x
      const cy = transformedPosition.y
      const cz = transformedPosition.z

      // 检查 AABB 的所有 8 个角
      for (let i = 0; i < 8; i++) {
        const px = i & 1 ? aabbMax[0] : aabbMin[0]
        const py = i & 2 ? aabbMax[1] : aabbMin[1]
        const pz = i & 4 ? aabbMax[2] : aabbMin[2]

        const dx = px - cx
        const dy = py - cy
        const dz = pz - cz

        const distSq = dx * dx + dy * dy + dz * dz
        const dist = Math.sqrt(distSq) * scale

        if (dist > maxDist) maxDist = dist
      }
    }

    // 径向排序的 minDist 始终为 0
    if (maxDist < 0) {
      maxDist = 0
    }

    return { minDist: 0, maxDist }
  }

  /**
   * 评估排序键（Linear Sort）
   */
  function evaluateSortKeysLinear(
    sortParams: SplatSortParams[],
    minDist: number,
    range: number,
    distances: Uint32Array,
    layoutIndexMap: Uint32Array,
    outputIndexMap: Uint32Array,
    countBuffer: Uint32Array,
    centersData: CentersData,
    compact?: SortVisibilityCompactOptions
  ): number {
    const { intervals, ids } = centersData
    const invBinRange = NUM_BINS / range

    let writeIndex = 0
    let compactIndex = 0

    // 遍历每个 splat
    for (let paramIdx = 0; paramIdx < sortParams.length; paramIdx++) {
      const params = sortParams[paramIdx]
      const id = ids[paramIdx]

      // 获取 centers
      const centers = centersMap.get(id)
      if (!centers) {
        throw new Error(`No centers found for id ${id}`)
      }
      const bounds = boundsMap.get(id)

      // 相机相关参数
      const { transformedDirection, offset, scale } = params
      const sdx = transformedDirection.x * scale
      const sdy = transformedDirection.y * scale
      const sdz = transformedDirection.z * scale
      const add = offset - minDist

      // 区间数组
      const intervalsArray =
        intervals[paramIdx].length > 0 ? intervals[paramIdx] : [0, centers.length / 3]

      // 遍历所有区间
      for (let i = 0; i < intervalsArray.length; i += 2) {
        const intervalStart = intervalsArray[i] * 3
        const intervalEnd = intervalsArray[i + 1] * 3

        // 处理区间内的每个 center
        for (let srcIndex = intervalStart; srcIndex < intervalEnd; srcIndex += 3) {
          const x = centers[srcIndex]
          const y = centers[srcIndex + 1]
          const z = centers[srcIndex + 2]
          const targetIndex = layoutIndexMap[compactIndex++]

          if (!shouldKeepForVisibilityCompact(params, x, y, z, compact, bounds, srcIndex / 3)) {
            continue
          }

          const dist = x * sdx + y * sdy + z * sdz + add

          // Bin-based mapping
          const d = dist * invBinRange
          const bin = d >>> 0
          const sortKey = (binBase[bin] + binDivider[bin] * (d - bin)) >>> 0

          distances[writeIndex] = sortKey
          outputIndexMap[writeIndex] = targetIndex
          writeIndex++
          countBuffer[sortKey]++
        }
      }
    }

    return writeIndex
  }

  /**
   * 评估排序键（Radial Sort）
   */
  function evaluateSortKeysRadial(
    sortParams: SplatSortParams[],
    _minDist: number,
    range: number,
    distances: Uint32Array,
    layoutIndexMap: Uint32Array,
    outputIndexMap: Uint32Array,
    countBuffer: Uint32Array,
    centersData: CentersData,
    compact?: SortVisibilityCompactOptions
  ): number {
    const { intervals, ids } = centersData
    const invBinRange = NUM_BINS / range

    let writeIndex = 0
    let compactIndex = 0

    // 遍历每个 splat
    for (let paramIdx = 0; paramIdx < sortParams.length; paramIdx++) {
      const params = sortParams[paramIdx]
      const id = ids[paramIdx]

      // 获取 centers
      const centers = centersMap.get(id)
      if (!centers) {
        throw new Error(`No centers found for id ${id}`)
      }
      const bounds = boundsMap.get(id)

      // 相机位置（本地空间）
      const cx = params.transformedPosition.x
      const cy = params.transformedPosition.y
      const cz = params.transformedPosition.z
      const scale = params.scale

      // 区间数组
      const intervalsArray =
        intervals[paramIdx].length > 0 ? intervals[paramIdx] : [0, centers.length / 3]

      // 遍历所有区间
      for (let i = 0; i < intervalsArray.length; i += 2) {
        const intervalStart = intervalsArray[i] * 3
        const intervalEnd = intervalsArray[i + 1] * 3

        // 处理区间内的每个 center
        for (let srcIndex = intervalStart; srcIndex < intervalEnd; srcIndex += 3) {
          const x = centers[srcIndex]
          const y = centers[srcIndex + 1]
          const z = centers[srcIndex + 2]
          const targetIndex = layoutIndexMap[compactIndex++]

          if (!shouldKeepForVisibilityCompact(params, x, y, z, compact, bounds, srcIndex / 3)) {
            continue
          }

          const dx = x - cx
          const dy = y - cy
          const dz = z - cz

          const distSq = dx * dx + dy * dy + dz * dz
          const dist = Math.sqrt(distSq) * scale

          // Bin-based mapping（反转距离以实现 back-to-front 渲染）
          const invertedDist = range - dist
          const d = invertedDist * invBinRange
          const bin = d >>> 0
          const sortKey = (binBase[bin] + binDivider[bin] * (d - bin)) >>> 0

          distances[writeIndex] = sortKey
          outputIndexMap[writeIndex] = targetIndex
          writeIndex++
          countBuffer[sortKey]++
        }
      }
    }

    return writeIndex
  }

  /**
   * Counting Sort（累积计数 + 构建输出）
   */
  function countingSort(
    bucketCount: number,
    countBuffer: Uint32Array,
    numVertices: number,
    distances: Uint32Array,
    indexMap: Uint32Array,
    order: Uint32Array
  ): void {
    // 累积计数
    for (let i = 1; i < bucketCount; i++) {
      countBuffer[i] += countBuffer[i - 1]
    }

    const validCount = countBuffer[bucketCount - 1]
    if (validCount !== numVertices && !_warnedSortKeyOverflow) {
      _warnedSortKeyOverflow = true
      __emitLog(2, `${numVertices - validCount} 个 splat 因 sortKey 越界丢失，请检查 Resource AABB`)
    }

    // 对齐冻结 PlayCanvas Unified Worker：正向消费 prefix end offset，
    // 因而在相同 sort key 内按输入逆序写出。
    for (let i = 0; i < numVertices; i++) {
      const distance = distances[i]
      const destIndex = --countBuffer[distance]
      order[destIndex] = indexMap[i]
    }
  }

  /**
   * 主排序函数
   */
  function sort(
    sortParams: SplatSortParams[],
    order: Uint32Array,
    centersData: CentersData,
    sortGeneration: number | undefined,
    compact?: SortVisibilityCompactOptions
  ): void {
    const startTime = Date.now()
    const numVertices = centersData.totalActiveSplats

    if (sortParams.length !== centersData.ids.length) {
      throw new Error(
        `GSplat sortParams 数量不匹配: actual=${sortParams.length}, required=${centersData.ids.length}`
      )
    }

    if (numVertices <= 0) {
      const orderBuffer = order.buffer as ArrayBuffer
      const response: WorkerResponse = {
        order: orderBuffer,
        count: 0,
        version: centersData.version,
        sortGeneration,
        totalActiveSplats: 0,
        culledSplats: 0,
        timeMs: Date.now() - startTime,
      }
      self.postMessage(response, [orderBuffer])
      return
    }

    // 计算距离范围
    const { minDist, maxDist } = _radialSort
      ? computeEffectiveDistanceRangeRadial(sortParams)
      : computeEffectiveDistanceRangeLinear(sortParams)

    // 计算 bucket 数量（动态 bit 分配）
    const compareBits = Math.max(10, Math.min(20, Math.round(Math.log2(numVertices / 4))))
    const bucketCount = 2 ** compareBits + 1

    // 分配或复用 distances buffer
    if (!distances || distances.length !== numVertices) {
      distances = new Uint32Array(numVertices)
    }

    if (!indexMap || indexMap.length !== numVertices) {
      throw new Error('GSplat indexMap 尚未按当前 intervals 构建')
    }
    if (!sortIndexMap || sortIndexMap.length !== numVertices) {
      sortIndexMap = new Uint32Array(numVertices)
    }

    // 分配或复用 countBuffer
    if (!countBuffer || countBuffer.length !== bucketCount) {
      countBuffer = new Uint32Array(bucketCount)
    } else {
      countBuffer.fill(0)
    }

    const range = Math.max(maxDist - minDist, 1e-6)

    // 设置相机相对的 bin 权重
    let cameraBin: number
    if (_radialSort) {
      // Radial Sort: 相机（dist=0）映射到最后一个 bin
      cameraBin = NUM_BINS - 1
    } else {
      // Linear Sort: 相机在投影空间中的位置
      const cameraOffsetFromRangeStart = 0 - minDist
      const cameraBinFloat = (cameraOffsetFromRangeStart / range) * NUM_BINS
      cameraBin = Math.max(0, Math.min(NUM_BINS - 1, Math.floor(cameraBinFloat)))
    }

    setupCameraRelativeBins(cameraBin, bucketCount)

    // 评估排序键
    let count = 0
    if (_radialSort) {
      count = evaluateSortKeysRadial(
        sortParams,
        minDist,
        range,
        distances,
        indexMap,
        sortIndexMap,
        countBuffer,
        centersData,
        compact
      )
    } else {
      count = evaluateSortKeysLinear(
        sortParams,
        minDist,
        range,
        distances,
        indexMap,
        sortIndexMap,
        countBuffer,
        centersData,
        compact
      )
    }

    // Counting Sort
    countingSort(bucketCount, countBuffer, count, distances, sortIndexMap, order)

    // 发送结果（使用 Worker 的 postMessage）
    const orderBuffer = order.buffer as ArrayBuffer
    const response: WorkerResponse = {
      order: orderBuffer,
      count,
      version: centersData.version,
      sortGeneration,
      totalActiveSplats: numVertices,
      culledSplats: numVertices - count,
      timeMs: Date.now() - startTime,
    }

    // Worker 上下文的 postMessage
    self.postMessage(response, [orderBuffer])
  }

  /**
   * Worker 消息处理
   */
  self.addEventListener('message', (message: MessageEvent<WorkerMessage>) => {
    const msgData = message.data

    try {
      switch (msgData.command) {
        // 添加 centers 到缓存
        case 'addCenters': {
          centersMap.set(msgData.id, new Float32Array(msgData.centers))
          if (msgData.bounds) {
            boundsMap.set(msgData.id, new Float32Array(msgData.bounds))
          } else {
            boundsMap.delete(msgData.id)
          }
          if (centersData) {
            const rebuiltIndexMap = buildIndexMap(centersData)
            if (rebuiltIndexMap) indexMap = rebuiltIndexMap
          }
          break
        }

        // 从缓存移除 centers
        case 'removeCenters': {
          centersMap.delete(msgData.id)
          boundsMap.delete(msgData.id)
          break
        }

        // 执行排序
        case 'sort': {
          _radialSort = msgData.radialSorting || false
          const order = new Uint32Array(msgData.order)

          if (!centersData) {
            __emitLog(1, 'sort called but centersData is null, aborting')
            // 返回空结果以避免卡住
            postEmptySortResult(msgData, 0, true)
            break
          }

          if (msgData.sortParams.length === 0) {
            postEmptySortResult(msgData, centersData.version)
            break
          }

          sort(
            msgData.sortParams,
            order,
            centersData,
            msgData.sortGeneration,
            msgData.visibilityCompact
          )
          break
        }

        // 接收 intervals 配置
        case 'intervals': {
          const nextCentersData: CentersData = {
            textureSize: msgData.textureSize,
            intervals: msgData.intervals,
            pixelOffsets: msgData.pixelOffsets,
            version: msgData.version,
            totalActiveSplats: msgData.totalActiveSplats,
            ids: msgData.ids,
          }
          const nextIndexMap = buildIndexMap(nextCentersData)
          centersData = nextCentersData
          indexMap = nextIndexMap
          break
        }
      }
    } catch (error) {
      __emitLog(1, `Error processing ${msgData.command}: ${getErrorMessage(error)}`)
      if (msgData.command === 'sort') {
        try {
          postEmptySortResult(msgData, centersData?.version ?? 0, true)
        } catch (postError) {
          __emitLog(1, `Error posting empty sort result: ${getErrorMessage(postError)}`)
        }
      }
    }
  })
}
